import { PipelineTab } from '../../../shared/model/pipeline-tab';
import { MedicalCase, MedicalCaseProcessingPhase } from '../../../domain/medical-case/model';
import { PreparationStepKey, ProcessingPhasePairKey } from './pipeline-step.model';
import type { FlowPipelineDefinition } from './pipeline-step.model';
import {
  WAYBACK_STEP_TO_PHASE,
  WAYBACK_PROCESSING_BOUNDARIES,
  WAYBACK_DOCUMENT_PHASE_ORDER,
  WAYBACK_ALL_PHASE_ORDER,
  WAYBACK_OUTDATED_RULES,
} from './wayback-phase-vocabulary';

export const WAYBACK_TABS: readonly Omit<PipelineTab, 'disabled'>[] = [
  { labelKey: 'DOMAIN.CASE.PIPELINE.TAB.UPLOAD', route: 'upload' },
  { labelKey: 'DOMAIN.CASE.PIPELINE.TAB.DIGITIZATION', route: 'digitization' },
  { labelKey: 'DOMAIN.CASE.PIPELINE.TAB.ANONYMIZE', route: 'anonymize' },
  { labelKey: 'DOMAIN.CASE.PIPELINE.TAB.SUMMARY', route: 'summary' },
  { labelKey: 'DOMAIN.CASE.PIPELINE.TAB.GLOSSARY', route: 'glossary' },
  { labelKey: 'DOMAIN.CASE.PIPELINE.TAB.FINAL_REPORT', route: 'final-report' },
  { labelKey: 'DOMAIN.CASE.PIPELINE.TAB.TRANSLATE', route: 'translate' },
  { labelKey: 'DOMAIN.CASE.PIPELINE.TAB.DEANONYMIZE', route: 'deanonymize' },
  { labelKey: 'DOMAIN.CASE.PIPELINE.TAB.PACKAGE', route: 'package' },
] as const;

export const WAYBACK_STEP_ORDER: readonly PreparationStepKey[] = [
  'upload',
  'digitization',
  'anonymize',
  'summary',
  'glossary',
  'final-report',
  'translate',
  'deanonymize',
  'package',
] as const;

export const WAYBACK_PROCESSING_PAIR_TO_STEP_MAP: Readonly<
  Partial<Record<ProcessingPhasePairKey, PreparationStepKey>>
> = Object.fromEntries(
  Object.entries(WAYBACK_PROCESSING_BOUNDARIES).map(
    ([step, [start, end]]) => [`${start}->${end}`, step]
  )
) as Record<ProcessingPhasePairKey, PreparationStepKey>;

export const WAYBACK_PHASE_TO_STEP_MAP: Readonly<
  Partial<Record<MedicalCaseProcessingPhase, PreparationStepKey>>
> = {
  SECOND_OPINION_INIT: 'upload',
  SECOND_OPINION_DIGITIZATION: 'digitization',
  SECOND_OPINION_ANONYMIZATION: 'anonymize',
  SECOND_OPINION_SUMMARY: 'summary',
  SECOND_OPINION_GLOSSARY: 'glossary',
  SECOND_OPINION_FINAL_REPORT: 'final-report',
  SECOND_OPINION_FINAL_REPORT_TRANSLATION: 'translate',
  SECOND_OPINION_FINAL_REPORT_DEANONYMIZATION: 'deanonymize',
  SECOND_OPINION_FINAL_REPORT_PDF: 'package',
};

export const WAYBACK_STEP_TO_PHASE_MAP = WAYBACK_STEP_TO_PHASE;

export const WAYBACK_PIPELINE_DEFINITION: FlowPipelineDefinition = {
  tabs: WAYBACK_TABS,
  stepOrder: WAYBACK_STEP_ORDER,
  defaultUnlockedSteps: ['upload'],
  processingPairToStepMap: WAYBACK_PROCESSING_PAIR_TO_STEP_MAP,
  phaseToStepMap: WAYBACK_PHASE_TO_STEP_MAP,
  stepToPhaseMap: WAYBACK_STEP_TO_PHASE_MAP,
  completionPhases: Object.entries(WAYBACK_STEP_TO_PHASE).map(
    ([step, phase]) => ({ step: step as PreparationStepKey, phase })
  ),
  fileIdResolvers: {
    summary: (mc: MedicalCase) => (mc.phases?.SECOND_OPINION_SUMMARY?.documents ?? []).length > 0,
    glossary: (mc: MedicalCase) => (mc.phases?.SECOND_OPINION_GLOSSARY?.documents ?? []).length > 0,
    'final-report': (mc: MedicalCase) => (mc.phases?.SECOND_OPINION_FINAL_REPORT?.documents ?? []).some(d => d.activeVersion?.fileId != null),
    translate: (mc: MedicalCase) => (mc.phases?.SECOND_OPINION_FINAL_REPORT_TRANSLATION?.documents ?? []).length > 0,
    deanonymize: (mc: MedicalCase) => (mc.phases?.SECOND_OPINION_FINAL_REPORT_DEANONYMIZATION?.documents ?? []).some(d => d.activeVersion?.fileId != null),
    package: (mc: MedicalCase) => (mc.phases?.SECOND_OPINION_FINAL_REPORT_PDF?.documents ?? []).some(d => d.activeVersion?.fileId != null),
  },
  outdatedRules: WAYBACK_OUTDATED_RULES,
  documentPhaseOrder: WAYBACK_DOCUMENT_PHASE_ORDER,
  allPhaseOrder: WAYBACK_ALL_PHASE_ORDER,
};
